import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Split from 'react-split';
import { makeStyles } from "@material-ui/core/styles";
import { Box, Paper } from '@material-ui/core';
import { deepCopy } from '../general';

import { SheetKinds } from '../redux/sheetDbSlice';
import { CardDb, CardDbHashType } from '../redux/cardDbSlice';
import { ArrowDb } from '../redux/arrowDbSlice';
import { updateUserCondition, UserCondition } from '../redux/userConditionSlice';
import useCurrentUserDb from '../hooks/useCurrentUserDb';
import useUserCondition from '../hooks/useUserCondition';
import useCurrentDb from '../hooks/useCurrentDb';
import useGetSheetValue from '../hooks/useGetSheetValue';
import useWindowSize from '@rehooks/window-size';

import { CoordinateType, getTimestamp } from '../general';
import Redirector from './Redirector';
import DigimagiAppBar from './DigimagiAppBar';
import RightMenu from './RightMenu';
import BookCover from './BookCover';
import WorkArea from './WorkArea';
import SheetInformation from './SheetInformation';
import SelectListEditer from './SelectListEditer';
import AddSheetDialog, { AddSheetRelation } from './AddSheetDialog';
import Note, { OpenNoteButton } from './Note';

export type WorkAreaStateType = 'bookCover' | 'sheet' | 'sheetInfo' | 'list';

export type CardMenuAction = {
  edit: (id: string) => void;
  childAdd: (id: string, selectingCardValue: CardDb) => void;
  resize: (id: string) => void;
  copy: (cardHash: CardDbHashType) => void;
  link: (id: string) => void;
  jump: (id: string) => void;
  forward: (id: string) => void;
  foreground: (id: string) => void;
  backward: (id: string) => void;
  backmost: (id: string) => void;
  delete: (id: string, sheetId?: string) => void;
}
export type ArrowMenuAction = {
  edit: (id: string) => void;
  link: (id: string) => void;
  jump: (id: string) => void;
  cutIn: (id: string) => void;
  delete: (id: string) => void;
}
export type BlankMenuDataType = CoordinateType & {
  copying: number;
} | undefined;

export type OperationMode = 'move' | 'connect';
export type ShowMenuType = { id: string, card: CardDb, anchorEl: EventTarget & HTMLElement, parentCardId?: string } | undefined;
export type ArrowMenuData = (CoordinateType & { id: string, arrow: ArrowDb }) | undefined;
export type HandlingStates = {
  showMenu: ShowMenuType;
  blankMenuData: BlankMenuDataType;
  arrowMenuData: ArrowMenuData;
  clickRect: CoordinateType | undefined;
  storyIndex: number | undefined;
  mode: OperationMode;
  connectId: string | undefined;
  selectingGrid: CoordinateType | undefined;
  selectedId: string | undefined;
  addCardPosition: CoordinateType | undefined;
}
export type HandlingStateSetters = {
  setShowMenu: (value: ShowMenuType) => void;
  setBlankMenuData: (value: BlankMenuDataType) => void;
  setArrowMenuData: (value: ArrowMenuData) => void;
  setClickRect: (Coordinate?: CoordinateType) => void;
  setStoryIndex: (ix: number | undefined) => void;
  setMode: (mode: OperationMode) => void;
  setConnectId: (id: string | undefined) => void;
  setSelectingGrid: (grid?: CoordinateType) => void;
  setSelectedId: (id: string | undefined) => void;
  setAddCardPosition: (value?: CoordinateType) => void;
}
export type CardAddActionState = { show: boolean, parent?: SheetKinds | string, selectingCardValue?: CardDb, cutInMode?: boolean, arrowId?: string | undefined };

const useStyles = makeStyles(() => ({
  split: {
    display: 'flex',
    height: '100%',
    cursor: 'ew-resize',
    zIndex: 3,
    position: 'relative',
  },
  menuPanel: {
    flex: 'auto',
    overflow: 'hidden',
    cursor: 'auto',
    zIndex: 0,
    position: 'relative',
  },
  workAreaPanel: {
    flex: 'auto',
    overflow: 'hidden',
    cursor: 'auto',
    zIndex: 2,
    position: 'relative',
  },
  notePanel: {
    flex: 'auto',
    overflow: 'auto',
    cursor: 'auto',
    zIndex: 1,
    position: 'relative',
    height: '90%',
  },
}));

const getSplitRatios = (condition?: UserCondition, currentId?: string | null, bookId?: string | null) => {
  if (!condition || !currentId || !bookId) return [30, 70];
  const ratios = condition.splitRatios.bookEditor;
  const noteDisplay = condition.noteDisplay.bookEditor[currentId];
  const workNoteRatios = condition.workNoteRatios.bookEditor[currentId] || [60, 40];

  if (condition.bookTreeDisplay) {
    const treeRatio = ratios[0];
    const workRatio = noteDisplay ? ratios[1] * workNoteRatios[0] / 100 : ratios[1];
    const noteRatio = ratios[1] * workNoteRatios[1] / 100;
    if (noteDisplay) return [treeRatio, workRatio, noteRatio];
    else return [treeRatio, workRatio];
  } else {
    const treeRatio = ratios[0];
    if (noteDisplay) return [treeRatio, ...workNoteRatios];
    else return [treeRatio, 100];
  }
}

type BookEditorProps = {
  redirected: boolean;
  setRedirected: (value: boolean) => void;
  setAppBarFrom: (from: '/' | '/book') => void;
}
const BookEditor: React.FC<BookEditorProps> = ({ redirected, setRedirected, setAppBarFrom }) => {
  const dispatch = useDispatch();
  const [userId] = useCurrentUserDb();
  const [userConditionId, userCondition] = useUserCondition(userId);
  const [currentSheetId, currentBookId] = useCurrentDb();
  const getSheetValue = useGetSheetValue();
  const [currentWorkId, setCurrentWorkId] = useState<string>();
  const [workArea, setWorkArea] = useState<WorkAreaStateType>('bookCover');
  const [sheetRedirected, setSheetRedirected] = useState(false);
  type AddSheetActionState = { show: boolean; relation?: AddSheetRelation };
  const [addSheetAction, setAddSheetAction] = useState<AddSheetActionState>({ show: false });
  const [cardAddAction, setCardAddAction] = useState<CardAddActionState>({ show: false, cutInMode: false });

  const windowSize = useWindowSize();

  const [showMenu, setShowMenu] = useState<ShowMenuType>(undefined);
  const [blankMenuData, setBlankMenuData] = useState<BlankMenuDataType>();
  const [arrowMenuData, setArrowMenuData] = useState<ArrowMenuData>();
  const [clickRect, setClickRect] = useState<CoordinateType>();
  const [storyIndex, setStoryIndex] = useState<number | undefined>(undefined);
  const [mode, setMode] = useState<OperationMode>('move');
  const [connectId, setConnectId] = useState<string | undefined>(undefined);
  const [selectingGrid, setSelectingGrid] = useState<CoordinateType>();
  const [splitRatios, setSplitRatios] = useState<number[]>(getSplitRatios(userCondition, currentWorkId, currentBookId));
  const [splitSizes, setSplitSizes] = useState<number[]>();
  const [noteDisplay, setNoteDisplay] = useState(false);
  const [noteLeft, setNoteLeft] = useState(0);
  const [noteTransition, setNoteTransition] = useState<string>();
  const noteTransitionTime = 180;
  const [selectedId, setSelectedId] = useState<string>();
  const [addCardPosition, setAddCardPosition] = useState<CoordinateType>();
  const [splitUpdated, setSplitUpdated] = useState(true);

  const classes = useStyles();

  const handlingStates: HandlingStates = {
    showMenu,
    blankMenuData,
    arrowMenuData,
    clickRect,
    storyIndex,
    mode,
    connectId,
    selectingGrid,
    selectedId,
    addCardPosition
  }
  const handlingStateSetters: HandlingStateSetters = {
    setShowMenu,
    setBlankMenuData,
    setArrowMenuData,
    setClickRect,
    setStoryIndex,
    setMode,
    setConnectId,
    setSelectingGrid,
    setSelectedId,
    setAddCardPosition
  }

  const initializeHandlingStates = () => {
    setShowMenu(undefined);
    setBlankMenuData(undefined);
    setArrowMenuData(undefined);
    setClickRect(undefined);
    setStoryIndex(undefined);
    setMode('move');
    setConnectId(undefined);
    setSelectingGrid(undefined);
    setSelectedId(undefined);
  }
  const selectSheet = (workArea: WorkAreaStateType, id?: string) => {
    setWorkArea(workArea);
    initializeHandlingStates();
    if (!userConditionId || !userCondition) return;
    if (workArea === 'bookCover') dispatch(updateUserCondition({ id: userConditionId, where: 'book', when: getTimestamp() }));
    else if (workArea === 'sheet' && id) dispatch(updateUserCondition({ id: userConditionId, where: 'sheet', when: getTimestamp(), sheetId: id }));
  }

  const dragSplit = (sizes: number[]) => {
    if (!userConditionId || !userCondition || !currentWorkId || !currentBookId) return;

    setSplitUpdated(false);

    const newRatio = deepCopy(userCondition.splitRatios);
    const newWorkNoteRatio = deepCopy(userCondition.workNoteRatios);
    if (!sizes) return;
    if (userCondition.bookTreeDisplay) {
      if (sizes[0] === newRatio.bookEditor[0]) {
        const coefficient = 100 / (sizes[1] + sizes[2]);
        const workRatio = sizes[1] * coefficient;
        const noteRatio = sizes[2] * coefficient;
        newWorkNoteRatio.bookEditor[currentWorkId] = [workRatio, noteRatio];
      } else {
        newRatio.bookEditor = [sizes[0], 100 - sizes[0]];
        const noteRatio = userCondition.workNoteRatios.bookEditor[currentWorkId] ? userCondition.workNoteRatios.bookEditor[currentWorkId][1] : 40;
        const appearanceNoteRatio = userCondition.splitRatios.bookEditor[1] * noteRatio;
        const newNoteRatio = appearanceNoteRatio / newRatio.bookEditor[1];
        newWorkNoteRatio.bookEditor[currentWorkId] = [100 - newNoteRatio, newNoteRatio];
      }
    } else {
      newWorkNoteRatio.bookEditor[currentWorkId] = [sizes[1], 100 - sizes[1]];
    }

    dispatch(updateUserCondition({ id: userConditionId, splitRatios: newRatio, workNoteRatios: newWorkNoteRatio }));
    setSplitRatios(getSplitRatios(userCondition, currentWorkId, currentBookId));

    setTimeout(() => setSplitUpdated(true), 1);
  }
  const toggleNote = (value: boolean) => {
    if (!userConditionId || !userCondition || !currentWorkId || value === noteDisplay) return;
    const newNoteDisplay = deepCopy(userCondition.noteDisplay);
    newNoteDisplay.bookEditor[currentWorkId] = value;

    const noteRatio = userCondition.workNoteRatios.bookEditor[currentWorkId] ? userCondition.workNoteRatios.bookEditor[currentWorkId][1] / 100 : 0.4;
    setNoteLeft(windowSize.innerWidth * userCondition.splitRatios.bookEditor[1] * noteRatio / 100);

    setNoteTransition(`${noteTransitionTime}ms ease-out`);
    if (value) setNoteDisplay(value);

    if (value) {
      dispatch(updateUserCondition({ id: userConditionId, noteDisplay: newNoteDisplay }));
      setTimeout(() => setNoteLeft(0), 1);
    } else {
      setTimeout(() => {
        dispatch(updateUserCondition({ id: userConditionId, noteDisplay: newNoteDisplay }));
        setNoteLeft(0);
      }, noteTransitionTime);
    }
    setTimeout(() => setNoteTransition(undefined), noteTransitionTime);
  }

  useEffect(() => {
    const currentId = workArea === 'bookCover' ? currentBookId : currentSheetId;
    setCurrentWorkId(currentId || undefined);
    setSplitUpdated(false);
    setTimeout(() => setSplitUpdated(true), 1);
  }, [userCondition, currentSheetId, currentBookId])

  useEffect(() => {
    if (!userConditionId || !userCondition) return;
    if (!sheetRedirected && userCondition.where === 'sheet' && userCondition.sheetId) {
      setWorkArea('sheet');
      setSheetRedirected(true);
    }
    if (currentWorkId) {
      const workNoteRatio = userCondition.workNoteRatios.bookEditor[currentWorkId];
      if (workNoteRatio && (isNaN(workNoteRatio[0]) || isNaN(workNoteRatio[1]) || !Boolean(workNoteRatio[0]) || !Boolean(workNoteRatio[1]))) {
        const newWorkNoteRatios = deepCopy(userCondition.workNoteRatios);
        newWorkNoteRatios.bookEditor[currentWorkId] = [60, 40];
        dispatch(updateUserCondition({ id: userConditionId, workNoteRatios: newWorkNoteRatios }));
        return;
      }
      setSplitRatios(getSplitRatios(userCondition, currentWorkId, currentBookId));
      setNoteDisplay(userCondition.noteDisplay.bookEditor[currentWorkId] || false);
    }
  }, [userCondition, currentWorkId])

  useEffect(() => {
    setSplitRatios(getSplitRatios(userCondition, currentWorkId, currentBookId));
  }, [noteDisplay])

  useEffect(() => {
    if (splitSizes) dragSplit(splitSizes);
  }, [splitSizes])

  useEffect(() => {
    setCardAddAction({ show: false, parent: getSheetValue(currentSheetId)?.sheetKind, cutInMode: false });
  }, [getSheetValue(currentSheetId)])

  if (!redirected) return <Redirector doneRedirect={() => setRedirected(true)} />;
  return (
    <>
      <Box style={{ overflow: 'hidden' }}>
        <DigimagiAppBar setRedirected={setRedirected} parent="Book" setAppBarFrom={() => setAppBarFrom('/book')} />
        <Paper style={{ height: windowSize.innerHeight - 64, backgroundColor: '#f3f3f3' }}>
          <Split
            minSize={220}
            expandToMin={true}
            gutterSize={5}
            gutterAlign="center"
            snapOffset={30}
            dragInterval={1}
            direction="horizontal"
            cursor="col-resize"
            sizes={splitRatios}
            onDrag={sizes => setSplitSizes(sizes)}
            className={classes.split}
          >
            <div className={classes.menuPanel} style={{ display: userCondition?.bookTreeDisplay ? undefined : 'none' }}>
              <RightMenu nodeClicked={(workArea: WorkAreaStateType, id) => selectSheet(workArea, id)}
                workAreaState={workArea}
                addSheetClicked={(relation) => setAddSheetAction({ show: true, relation })}
              />
            </div>
            <Paper className={classes.workAreaPanel} elevation={4}>
              { workArea === 'bookCover' && <BookCover /> }
              { workArea === 'sheet' &&
                <WorkArea infoClicked={(workArea: WorkAreaStateType) => setWorkArea(workArea)}
                  nodeClicked={(workArea: WorkAreaStateType, id) => selectSheet(workArea, id)}
                  handlingStates={handlingStates}
                  handlingStateSetters={handlingStateSetters}
                  addFabOffset={noteDisplay ? windowSize.innerWidth * splitRatios[2] / 100 + 32 : 16}
                  cardAddAction={cardAddAction}
                  setCardAddAction={setCardAddAction}
                />
              }
              { workArea === 'sheetInfo' && <SheetInformation completionClicked={() => setWorkArea('sheet')} 
                                              nodeClicked={(workArea: WorkAreaStateType, id) => selectSheet(workArea, id)} /> }
              { workArea === 'list' && <SelectListEditer /> }
              <OpenNoteButton noteDisplay={noteDisplay} openNote={() => toggleNote(true)} />
            </Paper>
            <div className={classes.notePanel} style={{ display: noteDisplay ? 'block' : 'none', left: noteLeft, transition: noteTransition }}>
              <Note
                reference={
                  workArea === 'bookCover' ? 'book' :
                  (workArea === 'sheet' || workArea === 'sheetInfo') ? 'sheet' :
                  'book'
                }
                toggleNote={() => toggleNote(false)}
                splitUpdated={splitUpdated}
                parent={cardAddAction.parent}
                index={storyIndex} coordinate={addCardPosition} grid={selectingGrid}
                clearSelectingGrid={() => setSelectingGrid(undefined)}
              />
            </div>
          </Split>
        </Paper>
      </Box>
      <AddSheetDialog open={addSheetAction.show} onClose={() => setAddSheetAction({show: false})} 
        workAreaState={workArea} 
        nodeClicked={(workArea: WorkAreaStateType, id) => selectSheet(workArea, id)}
        relation={addSheetAction.relation}
      />
    </>
  );
}

export default BookEditor;
